import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { ERROR_CATEGORIES, SEVERITIES } from '../constants/reviewCatalog';
import {
  formatDisplayDateTime,
  formatDuration,
  translationTypeLabel,
} from '../utils/adminMetrics';
import { getStatusClass, getStatusLabel } from '../utils/status';
import {
  getLanguagePairLabel,
  getTmDisplayName,
  resolveTmId,
} from '../utils/languages';

const PAGE_SIZE = 50;

function AdminTransactionTable({ transactions, loading, dateRangeLabel, availableTMs = [] }) {
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [sortKey, setSortKey] = useState('completed_date');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({
    completedDate: '',
    user: '',
    translationType: 'all',
    submissionType: 'all',
    status: 'all',
    errorCategory: 'all',
    severity: 'all',
  });

  useEffect(() => {
    setPage(0);
  }, [transactions]);

  const handleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey('completed_date'); setSortDir('desc');
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return <span className="sort-indicator sort-indicator--idle">↕</span>;
    return <span className="sort-indicator sort-indicator--active">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const filteredRows = useMemo(() => {
    return (transactions || []).filter((row) => {
      const completedAt = row.completed_date || row.received_date || '';
      if (filters.completedDate && !completedAt.startsWith(filters.completedDate)) {
        return false;
      }
      if (filters.user) {
        const needle = filters.user.toLowerCase();
        if (!(row.submitted_by || '').toLowerCase().includes(needle)) {
          return false;
        }
      }
      if (filters.translationType !== 'all') {
        const rowType = (row.translation_type || '').toLowerCase();
        if (filters.translationType === 'fuzzy-adapted') {
          if (rowType !== 'fuzzy-adapted' && rowType !== 'fuzzy') return false;
        } else if (rowType !== filters.translationType) {
          return false;
        }
      }
      if (filters.submissionType !== 'all') {
        const submissionType = row.submission_type || 'single';
        if (submissionType !== filters.submissionType) return false;
      }
      if (filters.status !== 'all' && row.status !== filters.status) {
        return false;
      }
      if (filters.errorCategory !== 'all' && row.error_category !== filters.errorCategory) {
        return false;
      }
      if (filters.severity !== 'all' && row.severity !== filters.severity) {
        return false;
      }
      return true;
    });
  }, [transactions, filters]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((a, b) => {
      const aVal = (a[sortKey] || '').toString().toLowerCase();
      const bVal = (b[sortKey] || '').toString().toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [filteredRows, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pageRows = sortedRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleExport = () => {
    if (filteredRows.length === 0) return;
    setExporting(true);
    try {
      const sheetRows = filteredRows.map((row) => ({
        'Completed At': row.completed_date || row.received_date || '',
        User: row.submitted_by || '',
        'Language Pair': getLanguagePairLabel(row),
        TM: getTmDisplayName(resolveTmId(row, availableTMs), availableTMs),
        'Translation Type': translationTypeLabel(row.translation_type),
        Submission: row.submission_type || 'single',
        Status: getStatusLabel(row.status),
        'Processing Time': formatDuration(row.processing_seconds),
        Review: row.review_status === 'approved' ? 'Approved' : 'Pending',
        'Error Category': row.error_category || '',
        Severity: row.severity || '',
        'Request ID': row.req_id || '',
      }));
      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `admin-translations-${dateRangeLabel || stamp}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="card admin-transactions-card">
      <div className="card-header admin-transactions-header">
        <div>
          <h2>Transaction History</h2>
          <p>Completed jobs in selected range, sorted by completion date</p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleExport}
          disabled={loading || exporting || filteredRows.length === 0}
        >
          {exporting ? 'Exporting…' : 'Export Excel'}
        </button>
      </div>

      <div className="table-wrapper">
        <table className="data-table admin-transactions-table">
          <thead>
            <tr>
              <th><button type="button" className="th-sort" onClick={() => handleSort('completed_date')}>Completed At {sortIndicator('completed_date')}</button></th>
              <th><button type="button" className="th-sort" onClick={() => handleSort('submitted_by')}>User {sortIndicator('submitted_by')}</button></th>
              <th>Language</th>
              <th>TM</th>
              <th><button type="button" className="th-sort" onClick={() => handleSort('translation_type')}>Type {sortIndicator('translation_type')}</button></th>
              <th><button type="button" className="th-sort" onClick={() => handleSort('submission_type')}>Submission {sortIndicator('submission_type')}</button></th>
              <th><button type="button" className="th-sort" onClick={() => handleSort('status')}>Status {sortIndicator('status')}</button></th>
              <th>Time</th>
              <th>Review</th>
              <th>Error Category</th>
              <th>Severity</th>
            </tr>
            <tr className="admin-table-filter-row">
              <th>
                <input
                  type="date"
                  className="admin-table-filter"
                  value={filters.completedDate}
                  onChange={(e) => handleFilterChange('completedDate', e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="admin-table-filter"
                  placeholder="Filter users…"
                  value={filters.user}
                  onChange={(e) => handleFilterChange('user', e.target.value)}
                />
              </th>
              <th />
              <th />
              <th>
                <select
                  className="admin-table-filter"
                  value={filters.translationType}
                  onChange={(e) => handleFilterChange('translationType', e.target.value)}
                >
                  <option value="all">All types</option>
                  <option value="exact">Exact</option>
                  <option value="fuzzy-adapted">Fuzzy</option>
                  <option value="custom">Custom</option>
                </select>
              </th>
              <th>
                <select
                  className="admin-table-filter"
                  value={filters.submissionType}
                  onChange={(e) => handleFilterChange('submissionType', e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="single">Single</option>
                  <option value="bulk">Bulk</option>
                </select>
              </th>
              <th>
                <select
                  className="admin-table-filter"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="success">Success</option>
                  <option value="processing">Processing</option>
                  <option value="retry_pending">Retry Scheduled</option>
                  <option value="error">Error</option>
                </select>
              </th>
              <th />
              <th />
              <th>
                <select
                  className="admin-table-filter"
                  value={filters.errorCategory}
                  onChange={(e) => handleFilterChange('errorCategory', e.target.value)}
                >
                  <option value="all">All</option>
                  {ERROR_CATEGORIES.map((item) => (
                    <option key={item.label} value={item.label}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  className="admin-table-filter"
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                >
                  <option value="all">All</option>
                  {SEVERITIES.map((item) => (
                    <option key={item.label} value={item.label}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={11} className="empty-state">Loading transactions…</td>
              </tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr>
                <td colSpan={11} className="empty-state">No transactions in this range.</td>
              </tr>
            )}
            {!loading && pageRows.map((row) => (
              <tr key={row.req_id}>
                <td>{formatDisplayDateTime(row.completed_date || row.received_date)}</td>
                <td className="admin-user-cell">{row.submitted_by || '—'}</td>
                <td>{getLanguagePairLabel(row, true)}</td>
                <td title={resolveTmId(row, availableTMs)}>
                  {getTmDisplayName(resolveTmId(row, availableTMs), availableTMs) || '—'}
                </td>
                <td>
                  <span className={`type-badge type-${(row.translation_type || 'unknown').replace('-adapted', '')}`}>
                    {translationTypeLabel(row.translation_type)}
                  </span>
                </td>
                <td>{row.submission_type === 'bulk' ? 'Bulk' : 'Single'}</td>
                <td>
                  <span className={`status-badge ${getStatusClass(row.status)}`}>
                    {getStatusLabel(row.status)}
                  </span>
                </td>
                <td>{formatDuration(row.processing_seconds)}</td>
                <td>{row.review_status === 'approved' ? 'Approved' : 'Pending'}</td>
                <td>{row.error_category || '—'}</td>
                <td>{row.severity || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && sortedRows.length > PAGE_SIZE && (
        <div className="pagination">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {page + 1} of {pageCount} · {sortedRows.length} rows
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminTransactionTable;
